import { ChangeDetectionStrategy, Component, computed, ElementRef, effect, inject, input, OnDestroy, output, signal } from '@angular/core';
import { dia } from '@clientio/rappid';
import { LegacySidebarService } from 'guideline/feature-sidebar';
import { BehaviorSubject } from 'rxjs';
import { provideGuideline, TreeScrollService } from 'shared/domain';
import { OrganisationSettingsService } from 'shared/util-common';

import { ColorCustomisationsDirective, CssVariableService } from '@treetop/shared/color-presets';
import { FlowchartNodeType, OrganisationSettings } from '@treetop/shared/model';

import { FlowchartComponent } from './flowchart.component';
import { FlowchartImageCacheService } from './services/flowchart-image-cache.service';

export type OffscreenFlowchartCaptureItem = {
  widgetId: string;
  elements: FlowchartNodeType[];
};

@Component({
  selector: 'tt-offscreen-flowchart-capture',
  standalone: true,
  imports: [FlowchartComponent, ColorCustomisationsDirective],
  providers: [
    ...provideGuideline(),
    TreeScrollService,
    {
      provide: LegacySidebarService,
      useValue: {
        isSidebarOpen: new BehaviorSubject<boolean>(false),
        searchResult: null,
        previousDescriptionLength: null,
        updateCurrentElement: () => undefined,
        updateCurrentElementForLink: () => undefined,
        updateFlowchart: () => undefined,
        decisionNodeHasOnlyTruePaths: () => false,
        isAttributeValueSet: () => false,
        isValueSet: () => false,
        openSidebar: () => undefined,
        closeSidebar: () => undefined,
        toggleSidebar: () => undefined,
        getUnitFromSourceLinkDecision: () => null,
        deepSearchElements: () => null,
      },
    },
  ],
  templateUrl: './offscreen-flowchart-capture.component.html',
  styleUrls: ['./offscreen-flowchart-capture.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OffscreenFlowchartCaptureComponent implements OnDestroy {
  private flowchartImageCache = inject(FlowchartImageCacheService);
  private cssVariableService = inject(CssVariableService);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly captureTimeoutMs = 10000;

  public items = input<OffscreenFlowchartCaptureItem[]>([]);
  public active = input<boolean>(false);
  public organisationSettingsOverride = input<OrganisationSettings | undefined>();
  public captured = output<Map<string, Blob>>();
  private organisationSettingsService = inject(OrganisationSettingsService);
  protected organisationSettings = computed(() => this.organisationSettingsOverride() ?? this.organisationSettingsService.settings());

  protected isReady = computed(() => this.flowchartImageCache.isReady());

  private hasEmittedForSession = signal(false);
  private captureTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private papers = new Map<string, dia.Paper>();

  constructor() {
    effect(() => {
      const isActive = this.active();
      const items = this.items();
      this.hasEmittedForSession.set(false);
      this.clearCaptureTimeout();

      if (!isActive || items.length === 0) {
        this.flowchartImageCache.clearAll();
        this.papers.clear();
        return;
      }

      const widgetIds = items.map((item) => item.widgetId);
      this.prunePapers(widgetIds);
      this.flowchartImageCache.clearAll();
      this.flowchartImageCache.setExpectedWidgetIds(widgetIds);
      this.captureAvailablePapers();
      this.captureTimeoutId = setTimeout(() => {
        if (!this.active() || this.hasEmittedForSession()) {
          return;
        }
        const widgetIds = this.items().map((item) => item.widgetId);
        this.captured.emit(this.flowchartImageCache.getMultiple(widgetIds));
        this.hasEmittedForSession.set(true);
      }, this.captureTimeoutMs);
    });

    effect(() => {
      if (!this.active() || !this.isReady() || this.hasEmittedForSession()) {
        return;
      }

      const widgetIds = this.items().map((item) => item.widgetId);
      const images = this.flowchartImageCache.getMultiple(widgetIds);

      this.clearCaptureTimeout();
      this.captured.emit(images);
      this.hasEmittedForSession.set(true);
    });

    effect(() => {
      this.applyColorPresets();
    });
  }

  public ngOnDestroy(): void {
    this.clearCaptureTimeout();
    this.flowchartImageCache.clearAll();
  }

  protected handlePaperReady(event: { flowchartId: string; paper: dia.Paper }): void {
    this.papers.set(event.flowchartId, event.paper);
    if (this.active() && !this.hasEmittedForSession()) {
      this.flowchartImageCache.renderFlowchart(event.flowchartId, event.paper);
    }
  }

  private clearCaptureTimeout(): void {
    if (this.captureTimeoutId) {
      clearTimeout(this.captureTimeoutId);
      this.captureTimeoutId = null;
    }
  }

  private captureAvailablePapers(): void {
    if (!this.active() || this.hasEmittedForSession()) {
      return;
    }

    this.items().forEach((item) => {
      const paper = this.papers.get(item.widgetId);
      if (paper) {
        this.flowchartImageCache.renderFlowchart(item.widgetId, paper);
      }
    });
  }

  private prunePapers(currentWidgetIds: string[]): void {
    const currentIds = new Set(currentWidgetIds);
    for (const id of [...this.papers.keys()]) {
      if (!currentIds.has(id)) {
        this.papers.delete(id);
      }
    }
  }

  private applyColorPresets(): void {
    const colorPresets = this.organisationSettings().customisations?.colorPresets;
    if (colorPresets) {
      this.cssVariableService.setColorPresets(colorPresets, this.elementRef.nativeElement);
    }
  }
}
